import type { Step } from './routine';

// Cue the upward phase just past halfway. With the longest production cadence this
// keeps either spoken gap under four seconds, while leaving controlled movement time.
export const UP_PHASE_FRACTION = 0.53;

export interface RepPacedCue {
  key: string;
  text: string;
  rep: number;
  phase: 'effort' | 'return';
}

/**
 * Selects the one cue appropriate to the current absolute point in a rep set.
 * It deliberately does not depend on speech end callbacks: iOS Web Speech may
 * delay or omit those callbacks, but the workout clock and cadence must not drift.
 */
export function repCueForElapsed(step: Step, elapsedMs: number): RepPacedCue | null {
  if (step.kind !== 'reps' || !step.cadenceMs || !step.reps) return null;
  const rep = Math.min(step.reps - 1, Math.max(0, Math.floor(elapsedMs / step.cadenceMs)));
  const withinRep = Math.max(0, elapsedMs - rep * step.cadenceMs);
  const isReturn = withinRep >= step.cadenceMs * UP_PHASE_FRACTION;

  if (isReturn) {
    return {
      key: `${rep}-return`,
      text: step.movement === 'bridge' ? 'Lower with control.' : 'Up.',
      rep,
      phase: 'return',
    };
  }

  const number = rep + 1;
  const side = step.eachSide ? (rep % 2 === 0 ? ' Left.' : ' Right.') : '';
  const progress = number === Math.ceil(step.reps / 2)
    ? ' Halfway.'
    : number === step.reps - 1
      ? ' Two left.'
      : '';
  const movement = step.movement === 'bridge' ? ' Lift and squeeze.' : ' Lower with control.';
  return {
    key: `${rep}-effort`,
    text: `${number}.${side}${movement}${progress}`,
    rep,
    phase: 'effort',
  };
}
